import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Form from '../../../common/Form';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { v4 as uuidv4 } from 'uuid';
import { useFieldArray, useFormContext, useForm } from 'react-hook-form';
import { FormButtons } from './FormButtons';
import { sendToastMessage } from '../../../actions/toast';
import { addApproversToPO, updatePoStatus, deleteApproversFromPO } from '../../../actions/payments';
import { validateStringValue } from '../../../utils/common';
import { getPoStatusColor } from '../../Payments/PaymentController';
import OverflowTip from '../../../common/OverflowTip';
import { convertIntoRupee } from '../../../utils/utils';
import { RenderTeamInput, RenderUserInput } from '../../../components/RaisePO/RaisePoForm';
import { closeModalAction, openCustomModal } from '../../../actions/modal';
import TextArea from '../../../common/Form/TextArea';


const updatePoApprover = `updatePoApprover`;


const approversArrayName = 'approvers';

export const expenseTypes = [
    { key: 'capex', value: 'CAPEX', label: 'Capex' },
    { key: 'opex', value: 'OPEX', label: 'Opex' },
];

export const actionType = {
    reject: 'REJECT',
    approve: 'APPROVE',
};

export const formName = {
    approveFormName: 'approve_po',
    rejectFormName: 'reject_po',
};


export const ApproveOrRejectRemark = ({ id, status }) => {
    const defaultValues = {};
    const methods = useForm({ defaultValues, mode: 'all' });
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);

    const onSubmit = (formValues) => {
        dispatch(updatePoStatus({ id, payload: { remark: formValues.remark, status: status }, formName: status === actionType.approve ? formName.approveFormName : formName.rejectFormName }));
    };

    return (
        <Form onCancel={() => dispatch(closeModalAction())} isLoading={formButtonLoading[status === actionType.approve ? formName.approveFormName : formName.rejectFormName]} showSubmit submitButtonText='Submit' submitContainerClassName='flex gap-2 justify-end py-4 px-4' methods={methods} onSubmit={onSubmit} className='w-full flex flex-col approvePOForm overflow-hidden'>
            <div className='flex gap-2 px-2'>
                <TextArea className='w-full' label='Remark' name='remark' type={'text'} />
            </div>
        </Form>
    );
};

export const PoApprovalForm = ({ poData }) => {
    const submitRef = useRef();
    const defaultValues = {};
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);

    const approveOrRejectPO = (formValues, action) => {

        dispatch(openCustomModal({
            modalComponent: <ApproveOrRejectRemark id={poData.id} status={action} />,
            heading: action === actionType.approve ? 'Approve PO' : 'Reject PO',
            modalWidth: '40rem',
            modalHeight: 'auto',
        }));
        // dispatch(updatePoStatus({ id: poData.id, payload: { remark: formValues.remark, status: action }, formName: action === actionType.approve ? formName.approveFormName : formName.rejectFormName }));
    };

    const methods = useForm({ defaultValues, mode: 'all' });

    const isLoading = formButtonLoading[updatePoApprover];

    return (
        <div className='w-full mx-auto flex flex-col overflow-hidden'>
            <Form
                onSubmit={(data) => {
                    approveOrRejectPO(data, actionType.approve);
                }}
                methods={methods}
                submitRef={submitRef}
                className='w-full flex flex-col approvePOForm overflow-hidden'
            >
                <div className='approvePOFormHeader'>
                    {/* Asset Details Card */}
                    <div className='bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden mb-4'>
                        <div className='bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200'>
                            <h2 className='text-lg font-semibold text-gray-800 flex items-center'>
                                <span className="material-icons text-scogoprimary mr-2">inventory_2</span>
                                Asset Details
                            </h2>
                        </div>
                        <div className='p-4'>
                            <AssetTable line_items={poData?.line_items ? poData?.line_items : []} />
                        </div>
                    </div>

                    {/* Approvers Section */}
                    <div className='bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden flex-1 flex flex-col'>
                        <div className='bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200 flex-shrink-0'>
                            <h2 className='text-lg font-semibold text-gray-800 flex items-center'>
                                <span className="material-icons text-scogoprimary mr-2">group</span>
                                Approval Workflow
                            </h2>
                        </div>
                        <div className='p-4 relative flex-1 overflow-hidden'>
                            {isLoading ? (
                                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 backdrop-blur-sm z-50 rounded-2xl">
                                    <div className="flex flex-col items-center space-y-4">
                                        <div className="relative">
                                            <div className="w-12 h-12 border-4 border-scogoprimary border-t-transparent rounded-full animate-spin"></div>
                                            <div className="absolute inset-0 w-12 h-12 border-4 border-scogoorange border-t-transparent rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                                        </div>
                                        <p className="text-scogoprimary font-medium">Updating approvers...</p>
                                    </div>
                                </div>
                            ) : null}

                            <div className='mb-3 flex-shrink-0'>
                                <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
                                    <div className='flex items-center'>
                                        <span className="material-icons text-blue-600 mr-2 text-sm">info</span>
                                        <p className='text-blue-800 font-medium text-xs'>Drag & drop rows to reorder approval priorities</p>
                                    </div>
                                </div>
                            </div>
                            <div className='relative bg-white rounded-lg shadow border border-gray-200 overflow-hidden flex-1 flex flex-col'>
                                {/* Enhanced Table Header */}
                                <div className='bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 flex-shrink-0'>
                                    <div className='flex gap-3 w-full py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                                        <div className='w-2/12 flex items-center'>
                                            <span className="material-icons text-gray-500 mr-1 text-sm">drag_indicator</span>
                                            Priority
                                        </div>
                                        <div className='w-4/12 flex items-center'>
                                            <span className="material-icons text-gray-500 mr-1 text-sm">group</span>
                                            Team
                                        </div>
                                        <div className='w-4/12 flex items-center'>
                                            <span className="material-icons text-gray-500 mr-1 text-sm">person</span>
                                            Users
                                        </div>
                                        <div className='w-2/12 flex items-center'>
                                            <span className="material-icons text-gray-500 mr-1 text-sm">check_circle</span>
                                            Status
                                        </div>
                                        <div className='w-2/12 flex items-center'>
                                            <span className="material-icons text-gray-500 mr-1 text-sm">settings</span>
                                            Actions
                                        </div>
                                    </div>
                                </div>
                                <div className='flex-1 overflow-y-auto max-h-64'>
                                    <ApproversTable approvers={poData?.approvers} po_id={poData?.id} org_id={poData?.org_id} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className='bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden mt-4'>
                        <div className='px-4 py-4'>
                            <FormButtons
                                isDisabled={poData?.approvers ? !poData?.approvers?.map((approver) => approver.user_id).includes(newLoggedUserData.user.id) : true}
                                submitRef={submitRef}
                                approveOrRejectPO={approveOrRejectPO}
                            />
                        </div>
                    </div>
                </div>
            </Form>
        </div>
    );
};

const ApproversTable = ({ approvers, po_id, org_id }) => {
    const { control, setValue } = useFormContext();
    const { fields, append, remove, swap } = useFieldArray({
        control,
        name: approversArrayName,
    });

    const memoizedApprovers = useMemo(() => {
        if (!approvers) return [];
        const approversByLevel = approvers.reduce((acc, approver) => {
            const level = approver.level || 0;

            if (!acc[level]) {
                acc[level] = {
                    level,
                    users: [],
                    teams: [],
                    status: approver.status || 'PENDING'
                };
            }

            if (approver.team_id && approver.team) {
                const teamExists = acc[level].teams.some(team => team.id === approver.team_id);
                if (!teamExists) {
                    acc[level].teams.push({
                        id: approver.team_id,
                        name: approver.team.name
                    });
                }
            } else if (approver.user_id && approver.user) {
                if (acc[level].teams.length === 0) {
                    const userExists = acc[level].users.some(user => user.id === approver.user_id);
                    if (!userExists) {
                        acc[level].users.push({
                            id: approver.user_id,
                            display_name: approver.user.display_name,
                            email: approver.user.email
                        });
                    }
                }
            }

            return acc;
        }, {});

        Object.values(approversByLevel).forEach(level => {
            if (level.teams.length > 0) {
                level.users = [];
            }
        });

        return Object.values(approversByLevel);
    }, [approvers]);

    useEffect(() => {
        if (memoizedApprovers) {
            setValue(approversArrayName, memoizedApprovers);
        }
    }, [memoizedApprovers, setValue]);

    const dispatch = useDispatch();

    const onRemove = (field, idx) => {
        if (field.isNew) remove(idx);
        else dispatch(sendToastMessage({ message: `Can't Delete`, status: 'danger' }));
    };

    function onDragEnd(result) {
        if (!result.destination) return;
        let sourceIndex = result.source.index;
        let destinationIndex = result.destination.index;
        swap(sourceIndex, destinationIndex);
    }

    const getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: 'none',
        backgroundColor: isDragging ? '#cae4f6' : 'white',
        ...draggableStyle,
    });

    return (
        <div className='relative'>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId='item_list'>
                    {(provided) => (
                        <ul {...provided.droppableProps} ref={provided.innerRef}>
                            {fields.map((field, index) => {
                                return (
                                    <Draggable key={field.id} draggableId={`item_list_${field.id}`} index={index}>
                                        {(provided, snapshot) => (
                                            <li
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                            >
                                                <ApproverItem approversArrayName={approversArrayName} field={field} index={index} onRemove={onRemove} po_id={po_id} org_id={org_id} />
                                            </li>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </ul>
                    )}
                </Droppable>
                <div className='px-3 py-2 bg-gray-50 border-t border-gray-200 flex-shrink-0'>
                    <button
                        type="button"
                        onClick={() => {
                            append({ id: uuidv4(), isNew: true, users: [], closed: 0, email: '' });
                        }}
                        style={{
                            fontSize: '0.9rem'
                        }}
                        className="group flex items-center justify-center px-4 py-2 text-sm font-semibold text-scogoprimary bg-gradient-to-r from-white to-gray-50 border-2 border-scogoprimary rounded-lg shadow hover:shadow-lg"
                    >
                        <span className="material-icons text-sm mr-2">add_circle</span>
                        Add New
                    </button>
                </div>
            </DragDropContext>
        </div>
    );
};




const ApproverItem = ({ field, index, approversArrayName, onRemove, org_id, po_id }) => {
    const { watch, setValue } = useFormContext();

    const dispatch = useDispatch();

    const team = watch(`${approversArrayName}[${index}].team`);
    const users = watch(`${approversArrayName}[${index}].users`);


    const getTeamColumn = ({ isNew }, idx) => {
        if (!isNew && Array.isArray(field.teams) && field.teams.length > 0) {
            return (
                <div className='flex flex-wrap gap-1'>
                    {field.teams.map((team) => (
                        <span key={team.id} className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-800 border border-blue-200'>
                            <span className="material-icons text-xs mr-1 text-blue-600">group</span>
                            {validateStringValue(team.name)}
                        </span>
                    ))}
                </div>
            );
        }

        if (isNew) {
            return (
                <RenderTeamInput
                    name={`${approversArrayName}[${idx}].team`}
                    org_id={org_id}
                    isDisabled={users && users.length > 0}
                />
            );
        }

        return <></>;
    };

    const getUsersColumn = ({ isNew }, idx) => {
        if (!isNew && Array.isArray(field.users) && field.users.length > 0) {
            return (
                <div className='flex flex-wrap gap-1'>
                    {field.users.map((user, userIdx) => (
                        <span
                            key={user.id}
                            className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-green-50 to-emerald-50 text-green-800 border border-green-200'
                        >
                            <span className="material-icons text-xs mr-1 text-green-600">person</span>
                            {`${validateStringValue(user.display_name)}`}
                        </span>
                    ))}
                </div>
            );
        }

        if (isNew) {
            console.log('isNew', isNew);
            console.log('org_id', org_id);
            return (
                <RenderUserInput
                    name={`${approversArrayName}[${idx}].users`}
                    org_id={org_id}
                    isDisabled={team && team.length > 0}
                />
            );
        }

        return <></>;
    };

    const onAddApprove = () => {
        dispatch(addApproversToPO({
            id: po_id, payload: {
                level: index + 1,
                users: users.length > 0 ? users.map(user => user.value) : [],
                team_id: team ? team.value : null
            }, formName: updatePoApprover
        }));

    };


    useEffect(() => {
        if (team && team.length > 0) {
            setValue(`${approversArrayName}[${index}].users`, []);
        }
    }, [team, setValue, approversArrayName, index]);

    useEffect(() => {
        if (users && users.length > 0) {
            setValue(`${approversArrayName}[${index}].team`, []);
        }
    }, [users, setValue, approversArrayName, index]);


    const onDeleteClick = () => {
        dispatch(deleteApproversFromPO({
            id: po_id,
            levelId: field.level,
            formName: updatePoApprover
        }));
    };

    return (
        <div key={field.id} className='group flex items-center gap-3 w-full border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-sm py-3 px-4 transition-all duration-300 hover:shadow-sm'>
            <div className='w-2/12 flex items-center gap-2'>
                <div className='flex items-center justify-center w-6 h-6 bg-gradient-to-r from-scogoprimary to-scogopriamry2 text-gray-900 rounded-full text-xs font-bold shadow'>
                    {index + 1}
                </div>
                <span className='material-icons text-gray-400 text-base cursor-move group-hover:text-scogoprimary transition-colors duration-300'>drag_indicator</span>
            </div>
            <div className='w-4/12'>
                <div className='space-y-1'>
                    {getTeamColumn(field, index)}
                </div>
            </div>
            <div className='w-4/12'>
                <div className='space-y-1'>
                    {getUsersColumn(field, index)}
                </div>
            </div>
            <div className='w-2/12'>
                <div className='flex items-center'>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getPoStatusColor(field?.status)}`}>
                        <span className="material-icons text-xs mr-1">
                            {field?.status === 'APPROVED' ? 'check_circle' :
                                field?.status === 'REJECTED' ? 'cancel' :
                                    field?.status === 'PENDING' ? 'schedule' : 'help'}
                        </span>
                        <OverflowTip someLongText={field?.status} />
                    </span>
                </div>
            </div>
            <div className='w-2/12 flex items-center gap-2'>
                {field.isNew && (
                    <div className='flex items-center space-x-1'>
                        <button
                            type="button"
                            onClick={() => onAddApprove()}
                            className="group flex items-center justify-center px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 rounded shadow hover:shadow-md transition-all duration-300 hover:from-green-600 hover:to-green-700 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-300"
                        >
                            <span className="material-icons text-sm mr-1 group-hover:animate-pulse">save</span>
                            Save
                        </button>
                        <button
                            type="button"
                            onClick={() => onRemove(field, index)}
                            className="group flex items-center justify-center px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded shadow hover:shadow-md transition-all duration-300 hover:from-red-600 hover:to-red-700 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-300"
                        >
                            <span className="material-icons text-sm mr-1 group-hover:animate-pulse">close</span>
                            Cancel
                        </button>
                    </div>
                )}

                {!field.isNew && (
                    <button
                        type="button"
                        onClick={onDeleteClick}
                        className="group flex items-center justify-center w-8 h-8 text-red-600 bg-red-50 rounded hover:bg-red-100 transition-all duration-300 transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-red-200"
                        title="Delete Approver"
                    >
                        <span className="material-icons text-base group-hover:animate-pulse">delete</span>
                    </button>
                )}
            </div>
        </div>
    );
};


const Thead = ({ children }) => {
    return (
        <th className='px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider'>
            <div className='flex items-center'>
                {children}
            </div>
        </th>
    );
};

const Tbody = ({ children }) => {
    return <td className='px-3 py-2 whitespace-nowrap text-sm text-gray-900'>{children}</td>;
};



const AssetTable = ({ line_items }) => {
    const totalQuantity = line_items?.reduce((acc, item) => acc + (item.quantity * convertIntoRupee(item.unit_price)), 0);

    return (
        <div className='overflow-hidden rounded-lg shadow border border-gray-200'>
            <table className='w-full'>
                <thead className='bg-gradient-to-r from-gray-50 to-gray-100'>
                    <tr className='border-b border-gray-200'>
                        <Thead><span className="material-icons text-gray-600 mr-1 text-base">inventory</span>Asset</Thead>
                        <Thead><span className="material-icons text-gray-600 mr-1 text-base">category</span>Category</Thead>
                        <Thead><span className="material-icons text-gray-600 mr-1 text-base">description</span>Description</Thead>
                        <Thead><span className="material-icons text-gray-600 mr-1 text-base">attach_money</span>Unit Price</Thead>
                        <Thead><span className="material-icons text-gray-600 mr-1 text-base">format_list_numbered</span>Quantity</Thead>
                    </tr>
                </thead>
                <tbody className='divide-y divide-gray-100 bg-white'>
                    {Array.isArray(line_items) &&
                        line_items?.map((item, index) => {
                            return (
                                <tr key={item._id} className='hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 group'>
                                    <Tbody>
                                        <div className='flex items-center'>
                                            <div className='w-6 h-6 bg-gradient-to-r from-blue-500 to-indigo-500 rounded flex items-center justify-center mr-2 group-hover:scale-110 transition-transform duration-300'>
                                                <span className="material-icons text-white text-sm">inventory_2</span>
                                            </div>
                                            <div className='truncate'>
                                                {item.item_title ? <OverflowTip someLongText={item.item_title} /> : <span className='text-gray-400 italic text-sm'>No title</span>}
                                            </div>
                                        </div>
                                    </Tbody>
                                    <Tbody>
                                        <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800'>
                                            {item.category ? <OverflowTip someLongText={item.category} /> : <span className='text-gray-400 italic'>No category</span>}
                                        </span>
                                    </Tbody>
                                    <Tbody>
                                        <div className='max-w-xs truncate'>
                                            {item.description ? <OverflowTip someLongText={item.description} /> : <span className='text-gray-400 italic text-sm'>No description</span>}
                                        </div>
                                    </Tbody>
                                    <Tbody>
                                        <span className='inline-flex items-center px-2 py-1 rounded font-semibold text-green-800 bg-green-100 border border-green-200 text-sm'>
                                            <span className="material-icons text-xs mr-1">currency_rupee</span>
                                            {item.unit_price ? convertIntoRupee(item.unit_price) : '0'}
                                        </span>
                                    </Tbody>
                                    <Tbody>
                                        <div className='flex items-center justify-center w-8 h-8 bg-gradient-to-r from-orange-100 to-yellow-100 rounded-full border border-orange-200'>
                                            <span className='text-orange-800 font-bold text-sm'>{item.quantity || 0}</span>
                                        </div>
                                    </Tbody>
                                </tr>
                            );
                        })}

                    <tr className='bg-gradient-to-r from-gray-50 to-gray-100 border-t-2 border-gray-200 font-bold'>
                        <Tbody>
                            <div className='flex items-center'>
                                <span className="material-icons text-scogoprimary mr-1 text-base">calculate</span>
                                <span className='text-base font-bold text-scogoprimary'>Total Amount</span>
                            </div>
                        </Tbody>
                        <Tbody></Tbody>
                        <Tbody></Tbody>
                        <Tbody></Tbody>
                        <Tbody>
                            <div className='flex items-center justify-center'>
                                <span className='inline-flex items-center px-3 py-2 rounded-lg font-bold text-lg text-white bg-gradient-to-r from-green-600 to-emerald-600 shadow'>
                                    <span className="material-icons text-base mr-1">currency_rupee</span>
                                    {totalQuantity || 0}
                                </span>
                            </div>
                        </Tbody>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};